// IRIS 2023 CKD Staging Logic for feline patients.
// Staging is based on stable, fasting creatinine (US mg/dL) measured on
// two separate occasions in a stable, well-hydrated patient. SDMA is
// supportive. Substaging by UPC (proteinuria) and systolic blood pressure.

export function stageFromCreatinine(mg_dl) {
  if (mg_dl == null || isNaN(mg_dl)) return null;
  if (mg_dl < 1.6) return 1;
  if (mg_dl <= 2.8) return 2;
  if (mg_dl <= 5.0) return 3;
  return 4;
}

export function stageFromSDMA(sdma_ug_dl) {
  if (sdma_ug_dl == null || isNaN(sdma_ug_dl)) return null;
  if (sdma_ug_dl < 14) return null;
  if (sdma_ug_dl < 18) return 1;
  if (sdma_ug_dl < 25) return 2;
  if (sdma_ug_dl < 45) return 3;
  return 4;
}

export function proteinuriaSub(upc) {
  if (upc == null || isNaN(upc)) return null;
  if (upc < 0.2) return { code: "NP", label: "Non-proteinuric" };
  if (upc <= 0.4) return { code: "BP", label: "Borderline proteinuric" };
  return { code: "P", label: "Proteinuric" };
}

export function bpSub(sbp) {
  if (sbp == null || isNaN(sbp)) return null;
  if (sbp < 140) return { code: "N", label: "Normotensive", risk: "Minimal" };
  if (sbp < 160) return { code: "LR", label: "Prehypertensive", risk: "Low risk of TOD" };
  if (sbp < 180) return { code: "MR", label: "Hypertensive", risk: "Moderate risk of TOD" };
  return { code: "HR", label: "Severely hypertensive", risk: "High risk of TOD" };
}

// Compute complete IRIS assessment given an ordered list of labs (oldest -> newest)
export function computeIRIS(labs) {
  const withCreat = labs.filter((l) => l.creatinine_mg_dl != null && !isNaN(l.creatinine_mg_dl));
  if (withCreat.length < 2) {
    return {
      ready: false,
      reason: `Need at least 2 lab sets with creatinine (currently ${withCreat.length}).`,
    };
  }
  // Use two most recent stable creatinine values, take the higher (conservative) for staging.
  const last2 = withCreat.slice(-2);
  const stableCreat = Math.max(last2[0].creatinine_mg_dl, last2[1].creatinine_mg_dl);
  const creatStage = stageFromCreatinine(stableCreat);

  // SDMA support (use most recent SDMA value)
  const sdmaLabs = labs.filter((l) => l.sdma_ug_dl != null && !isNaN(l.sdma_ug_dl));
  const latestSdma = sdmaLabs.length ? sdmaLabs[sdmaLabs.length - 1].sdma_ug_dl : null;
  const sdmaStage = stageFromSDMA(latestSdma);

  // Use max of the two stages (SDMA can bump when discordant)
  let stage = creatStage;
  let sdmaBumped = false;
  if (sdmaStage && sdmaStage > creatStage) {
    stage = sdmaStage;
    sdmaBumped = true;
  }

  // Substaging uses most recent UPC and SBP
  const latestUpcLab = [...labs].reverse().find((l) => l.upc != null && !isNaN(l.upc));
  const latestBpLab = [...labs].reverse().find((l) => l.bp_systolic != null && !isNaN(l.bp_systolic));

  return {
    ready: true,
    stage,
    stableCreatinine: stableCreat,
    latestSdma,
    sdmaBumped,
    proteinuria: latestUpcLab ? { ...proteinuriaSub(latestUpcLab.upc), upc: latestUpcLab.upc } : null,
    bp: latestBpLab ? { ...bpSub(latestBpLab.bp_systolic), sbp: latestBpLab.bp_systolic } : null,
  };
}

export const STAGE_LABELS = {
  1: "Stage 1 — At risk / Non-azotemic",
  2: "Stage 2 — Mild renal azotemia",
  3: "Stage 3 — Moderate renal azotemia",
  4: "Stage 4 — Severe renal azotemia / Uremia",
};

export function stageDescription(stage) {
  return {
    1: "Some other renal abnormality present (e.g., inadequate urinary concentrating ability without an identifiable non-renal cause, abnormal renal palpation, imaging findings, proteinuria of renal origin, or persistently elevated SDMA). Creatinine typically within reference range.",
    2: "Mild renal azotemia. Clinical signs are usually mild or absent.",
    3: "Moderate renal azotemia. Many systemic clinical signs may be present.",
    4: "Severe renal azotemia. Increasing risk of systemic clinical signs and uremic crises.",
  }[stage];
}

// Management recommendations per IRIS 2023 (summarised).
export function managementPlan(iris) {
  if (!iris || !iris.ready) return null;
  const s = iris.stage;
  const common = [
    "Encourage constant water intake (fountains, wet food, multiple bowls).",
    "Recheck IRIS staging in a stable, well-hydrated patient — do not stage during acute dehydration or illness.",
    "Investigate and treat pre-renal / post-renal contributors and comorbidities (hyperthyroidism, dental disease, UTI, urolithiasis).",
    "Screen for and treat systemic hypertension and proteinuria (see substaging).",
  ];
  const perStage = {
    1: [
      "Discontinue any nephrotoxic medications where feasible.",
      "Standard-nutrient adult diet is usually appropriate; renal diet not typically required at Stage 1.",
      "Address any identified underlying kidney disease.",
      "Monitor: recheck creatinine, SDMA, USG, UPC, and BP every 6–12 months.",
    ],
    2: [
      "Transition to a clinical renal diet (moderately reduced phosphorus & protein, high-quality protein, omega-3 supplemented) if the cat will eat it — palatability first.",
      "Target serum phosphorus <4.5 mg/dL (<1.45 mmol/L); add an intestinal phosphate binder if not achieved on diet alone.",
      "Screen for and manage dehydration; consider subcutaneous fluids intermittently if clinically dehydrated.",
      "Monitor: reassess every 3–6 months.",
    ],
    3: [
      "Clinical renal diet is strongly indicated.",
      "Target serum phosphorus <5.0 mg/dL (<1.6 mmol/L); phosphate binders commonly required.",
      "Assess and treat metabolic acidosis (consider potassium citrate / bicarbonate).",
      "Supplement potassium if hypokalaemic (potassium gluconate).",
      "Manage anaemia — check PCV, iron status; consider ESA (darbepoetin) once other causes excluded.",
      "Consider antiemetics (maropitant, ondansetron) and appetite stimulants (mirtazapine, capromorelin) as needed.",
      "Subcutaneous fluid therapy is commonly needed to maintain hydration.",
      "Monitor: reassess every 1–3 months.",
    ],
    4: [
      "Intensive uraemia management — antiemetics, gastroprotectants (omeprazole), appetite stimulants.",
      "Target serum phosphorus <6.0 mg/dL (<1.9 mmol/L) — phosphate binders almost always required.",
      "Aggressive management of dehydration; subcutaneous or IV fluids as tolerated.",
      "Manage anaemia — ESA therapy commonly indicated.",
      "Nutritional support via feeding tube may be appropriate to maintain caloric intake and quality of life.",
      "Frank discussion with caretaker regarding prognosis, quality-of-life scoring, and end-of-life planning.",
      "Monitor: reassess every 2–4 weeks or as clinically indicated.",
    ],
  }[s];

  const proteinuria = iris.proteinuria
    ? {
        NP: "Non-proteinuric (UPC <0.2): no specific proteinuria therapy required. Recheck UPC at each staging.",
        BP: "Borderline proteinuric (UPC 0.2–0.4): recheck UPC in 2 months. Investigate for pre-/post-renal causes. If persistent, treat as proteinuric.",
        P: "Proteinuric (UPC >0.4): start an ACE inhibitor (benazepril) or angiotensin receptor blocker (telmisartan). Ensure the patient is not dehydrated before starting. Recheck UPC, creatinine, and electrolytes within 1–2 weeks.",
      }[iris.proteinuria.code]
    : "UPC not measured — consider adding a urine protein:creatinine ratio to fully substage.";

  const bp = iris.bp
    ? {
        N: "Normotensive (SBP <140 mmHg): recheck BP at each staging.",
        LR: "Prehypertensive (SBP 140–159 mmHg): recheck within 1–2 months. Screen for target organ damage.",
        MR: "Hypertensive (SBP 160–179 mmHg): initiate antihypertensive therapy — amlodipine is first-line in cats. Recheck within 7–14 days.",
        HR: "Severely hypertensive (SBP ≥180 mmHg): treat urgently with amlodipine. Assess for ocular / neurological target-organ damage. Recheck within 3–7 days.",
      }[iris.bp.code]
    : "Systolic blood pressure not recorded — measure BP to complete substaging (indirect Doppler or oscillometric, cat in a calm environment).";

  return { common, perStage, proteinuria, bp };
}
